import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Label,
  Tooltip
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface BullCallSpreadProps {
  symbol: string;
  currentPrice?: number;
}

const BullCallSpread: React.FC<BullCallSpreadProps> = ({ 
  symbol, 
  currentPrice = 100 // Default value if not provided
}) => {
  // Generate options data - in a real app this would come from an API
  const lowerStrike = Math.round(currentPrice * 0.95); // 5% below current price
  const upperStrike = Math.round(currentPrice * 1.05); // 5% above current price
  const lowerPremium = Math.round(currentPrice * 0.03); // 3% of current price
  const upperPremium = Math.round(currentPrice * 0.015); // 1.5% of current price
  const netPremium = lowerPremium - upperPremium;
  const breakEvenPrice = lowerStrike + netPremium;
  const maxProfit = upperStrike - lowerStrike - netPremium;
  const maxLoss = netPremium;

  // Generate payoff diagram data
  const generatePayoffData = () => {
    const startPrice = currentPrice * 0.8; // Start at 80% of current price
    const endPrice = currentPrice * 1.2; // End at 120% of current price
    const step = (endPrice - startPrice) / 20;
    
    const data = [];
    for (let price = startPrice; price <= endPrice; price += step) {
      let payoff;
      if (price <= lowerStrike) {
        payoff = -netPremium; // Max loss if price is below lower strike
      } else if (price >= upperStrike) {
        payoff = maxProfit; // Max profit if price is above upper strike
      } else {
        payoff = price - lowerStrike - netPremium; // Linear payoff between strikes
      }
      
      data.push({ price: Math.round(price), payoff: Math.round(payoff * 100) / 100 });
    }
    
    return data;
  };

  const payoffData = generatePayoffData();

  return (
    <Card className="shadow-sm border-t-4 border-t-blue-500">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">
            Bull Call Spread for {symbol}
            <Badge variant="secondary" className="ml-2">Bullish Strategy</Badge>
          </CardTitle>
        </div>
        <CardDescription>
          A bull call spread is created by buying a call option at a lower strike price and selling a call option at a higher strike price.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-base font-medium mb-2">Strategy Overview</h3>
            <p className="text-sm text-gray-700 mb-4">
              This strategy is used when you expect a moderate increase in the price of {symbol}. It offers limited profit potential but reduces the cost of buying the call option.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Buy Call</h4>
                <p className="text-xl font-bold">${lowerStrike}</p>
                <p className="text-xs text-gray-500">Premium: ${lowerPremium}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Sell Call</h4>
                <p className="text-xl font-bold">${upperStrike}</p>
                <p className="text-xs text-gray-500">Premium: ${upperPremium}</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">Max Profit</h4>
                <p className="text-xl font-bold text-green-600">${maxProfit}</p>
                <p className="text-xs text-gray-500">At price ≥ ${upperStrike}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">Max Loss</h4>
                <p className="text-xl font-bold text-red-600">${maxLoss}</p>
                <p className="text-xs text-gray-500">Limited to net premium</p>
              </div>
            </div>
            
            <div className="mt-4 bg-blue-50 p-3 rounded-lg">
              <h4 className="text-sm font-medium text-blue-700">Break-even Price</h4>
              <p className="text-lg font-bold">${breakEvenPrice}</p>
              <p className="text-xs text-gray-600">Lower strike + net premium</p>
            </div>
          </div>
          
          <div>
            <h3 className="text-base font-medium mb-2">Payoff Diagram</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={payoffData}
                margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="price" 
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 12 }}
                >
                  <Label value="Stock Price" position="bottom" offset={0} />
                </XAxis>
                <YAxis
                  domain={[Math.min(-maxLoss*1.2, -10), Math.max(maxProfit*1.2, 10)]}
                  tick={{ fontSize: 12 }}
                >
                  <Label value="Profit/Loss" position="left" angle={-90} offset={-5} style={{ textAnchor: 'middle' }} />
                </YAxis>
                <Tooltip formatter={(value) => [`$${value}`, 'Profit/Loss']} labelFormatter={(label) => `Price: $${label}`} />
                
                {/* Zero reference line */}
                <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={1} />
                
                {/* Strike prices reference lines */}
                <ReferenceLine x={lowerStrike} stroke="#6366f1" strokeDasharray="3 3">
                  <Label value={`Lower Strike: $${lowerStrike}`} position="top" fill="#6366f1" fontSize={11} />
                </ReferenceLine>
                <ReferenceLine x={upperStrike} stroke="#8b5cf6" strokeDasharray="3 3">
                  <Label value={`Upper Strike: $${upperStrike}`} position="top" fill="#8b5cf6" fontSize={11} />
                </ReferenceLine>
                
                {/* Current price reference line */}
                <ReferenceLine x={currentPrice} stroke="#10b981" strokeWidth={2}>
                  <Label value={`Current: $${currentPrice}`} position="bottom" fill="#10b981" fontSize={11} />
                </ReferenceLine>
                
                {/* Break-even reference line */}
                <ReferenceLine x={breakEvenPrice} stroke="#3b82f6" strokeDasharray="5 5">
                  <Label value={`Break-even: $${breakEvenPrice}`} position="insideBottomLeft" fill="#3b82f6" fontSize={11} />
                </ReferenceLine>
                
                {/* Profit/loss line */}
                <Line
                  type="monotone"
                  dataKey="payoff"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
            
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-800">How It Works</h4>
              <ul className="text-xs text-gray-700 space-y-1 mt-1 list-disc pl-4">
                <li>Buy a call option at the ${lowerStrike} strike price</li>
                <li>Sell a call option at the ${upperStrike} strike price</li>
                <li>Both options have the same expiration date</li>
                <li>Maximum profit is achieved when {symbol} is at or above ${upperStrike}</li>
                <li>Maximum loss occurs when {symbol} is below ${lowerStrike}</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BullCallSpread;